/**
 * Navigation Component
 *
 * Sidebar navigation for the Tab Manager admin interface.
 *
 * @since 1.0.0
 */

import { GlobeAltIcon, QueueListIcon } from '@heroicons/react/24/outline';
import { Button } from '@redfoxwp/core-components/components/button';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { Modal, type ModalStrings } from '@redfoxwp/core-components/components/modal';
import { __ } from '@wordpress/i18n';
import { useState } from 'react';
import { useHashLocation } from 'wouter/use-hash-location';

import { useNavigationGuard } from '../../hooks';
import { NavigationItem, NavigationItemIcon, NavigationItemLabel, NavigationMenu } from './styled';

/**
 * Protected Navigation Item component that checks for unsaved changes
 *
 * @since 1.0.0
 */
const ProtectedNavigationItem = ({
  path,
  label,
  description,
  icon,
}: {
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}): JSX.Element => {
  const [location, setLocation] = useHashLocation();
  const { hasUnsavedChanges } = useNavigationGuard();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const isActive = location === path;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // If we're already on this path, don't do anything
    if (location === path) {
      return;
    }

    // Only show confirmation modal if:
    // 1. There are unsaved changes AND
    // 2. User is currently on the global-tabs page (leaving global tabs)
    if (hasUnsavedChanges && location === '/global-tabs') {
      event.preventDefault();
      setPendingPath(path);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmNavigation = () => {
    if (pendingPath) {
      setLocation(pendingPath);
      setPendingPath(null);
    }
    setShowConfirmModal(false);
  };

  const handleCancelNavigation = () => {
    setPendingPath(null);
    setShowConfirmModal(false);
  };

  // Create localized strings for the modal
  const modalStrings: ModalStrings = {
    closeModal: __('Close modal', 'redfox-tab-manager'),
  };

  return (
    <>
      <NavigationItem to={path} title={description} onClick={handleClick} $isActive={isActive}>
        <NavigationItemIcon>{icon}</NavigationItemIcon>
        <NavigationItemLabel>{label}</NavigationItemLabel>
      </NavigationItem>

      <Modal
        isOpen={showConfirmModal}
        onClose={handleCancelNavigation}
        title={__('Unsaved Changes', 'redfox-tab-manager')}
        strings={modalStrings}
        footer={
          <Flex gap='10px' justify='flex-end'>
            <Button variant='outline' onClick={handleCancelNavigation}>
              {__('Cancel', 'redfox-tab-manager')}
            </Button>
            <Button variant='solid' onClick={handleConfirmNavigation}>
              {__('Leave Page', 'redfox-tab-manager')}
            </Button>
          </Flex>
        }
      >
        <p>
          {__(
            'You have unsaved changes in Global Tabs. If you leave now, your changes will be lost. Are you sure you want to continue?',
            'redfox-tab-manager'
          )}
        </p>
      </Modal>
    </>
  );
};

/**
 * Navigation component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const Navigation = (): JSX.Element => {
  const navigationItems = [
    {
      path: '/tabs',
      label: __('Tab List', 'redfox-tab-manager'),
      description: __('Manage individual tabs', 'redfox-tab-manager'),
      icon: <QueueListIcon width={20} height={20} />,
    },
    {
      path: '/global-tabs',
      label: __('Global Tabs', 'redfox-tab-manager'),
      description: __('Configure global tab settings', 'redfox-tab-manager'),
      icon: <GlobeAltIcon width={20} height={20} />,
    },
  ];

  return (
    <NavigationMenu>
      {navigationItems.map((item) => (
        <ProtectedNavigationItem
          key={item.path}
          path={item.path}
          label={item.label}
          description={item.description}
          icon={item.icon}
        />
      ))}
    </NavigationMenu>
  );
};
